// src/oura/newscan/reask.ts
//
// The logic behind Re-ask N questions (components/newscan/Reask.tsx): which
// edits can skip the full rescan, and the run that dry-runs every correction,
// then sends them one at a time and waits for each to be asked.

import { useCallback, useEffect, useRef, useState } from "react";
import type { ScanDraft, ScanDraftQuestion } from "@service/aeo/scanDraftTypes";
import { correctQuestion, getQuestionCorrections } from "@service/aeo";

/** At most this many questions go one by one; more is a rewrite, and the full rescan is cheaper to reason about. */
export const REASK_MAX = 10;

// Ipul's handoff (20 Oct): poll about every 10 seconds; a re-ask takes a few minutes.
const POLL_MS = 10_000;
const WAIT_MS = 6 * 60 * 1000;


export type RowState = "waiting" | "asking" | "asked" | "failed";

/** Only reworded questions changed, few enough to re-ask one by one: the edit can skip the full rescan. */
export function reaskQuestions(draft: ScanDraft): ScanDraftQuestion[] | null {
    if (draft.kind !== "edit_set" || !draft.sourceAssessmentId || draft.stale) return null;
    const changed = draft.questions.filter((q) => q.changed);
    if (changed.length === 0 || changed.length > REASK_MAX) return null;
    // A word or a business change rewrites questions too: those need the whole check again.
    return changed.every((q) => q.reworded) ? changed : null;
}

export interface Reask {
    phase: "idle" | "checking" | "refused" | "running" | "done";
    rows: Array<{ q: ScanDraftQuestion; state: RowState; error: string | null }>;
    confirming: boolean;
    setConfirming(open: boolean): void;
    run(): Promise<void>;
}

export function useReask(draft: ScanDraft | null, questions: ScanDraftQuestion[]): Reask {
    const [phase, setPhase] = useState<Reask["phase"]>("idle");
    const [confirming, setConfirming] = useState(false);
    const [rows, setRows] = useState<Reask["rows"]>([]);
    const running = useRef(false);
    const assessmentId = draft?.sourceAssessmentId ?? "";
    // A run belongs to one scan and one mounted Review (Jack, #4468): another scan, or leaving the
    // page, ends it. An ended run stops before its next paid call, not only before its next render:
    // a correction the server already accepted cannot be taken back, but no further one is sent.
    const generation = useRef(0);
    useEffect(() => {
        const active = ++generation.current;
        running.current = false;
        setPhase("idle");
        setConfirming(false);
        setRows([]);
        return () => {
            if (generation.current === active) {
                generation.current += 1;
                running.current = false;
            }
        };
    }, [assessmentId]);

    const patch = (gen: number, n: number, next: Partial<Reask["rows"][number]>) => {
        if (gen !== generation.current) return;
        setRows((rs) => rs.map((r) => (r.q.n === n ? { ...r, ...next } : r)));
    };
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    /** Until the correction settles, or the run ends. `clear_failed`: the old answers were not cleared, so it may be sent again. */
    const waitFor = async (n: number, cancelled: () => boolean): Promise<RowState | "clear_failed" | "cancelled"> => {
        const until = Date.now() + WAIT_MS;
        while (Date.now() < until) {
            if (cancelled()) return "cancelled";
            await sleep(POLL_MS);
            if (cancelled()) return "cancelled";
            try {
                const state = (await getQuestionCorrections(assessmentId))[String(n)]?.state;
                if (cancelled()) return "cancelled";
                if (state === "asked") return "asked";
                if (state === "clear_failed") return "clear_failed";
                if (state === "failed") return "failed";
            } catch {
                // A failed read is not a failed re-ask: keep waiting.
            }
        }
        return "failed";
    };

    const run = useCallback(async () => {
        if (running.current || !assessmentId) return;
        running.current = true;
        const gen = generation.current;
        const stale = () => gen !== generation.current;
        setConfirming(false);
        // A retry sends only what is not asked yet.
        const todo = phase === "done" ? rows.filter((r) => r.state !== "asked").map((r) => r.q) : questions;
        setRows((rs) => (phase === "done" ? rs.map((r) => (r.state === "asked" ? r : { ...r, state: "waiting", error: null })) : questions.map((q) => ({ q, state: "waiting", error: null }))));
        try {
            setPhase("checking");
            const refused: Array<[number, string]> = [];
            for (const q of todo) {
                if (stale()) return;
                const res = await correctQuestion(assessmentId, q.n, q.text, { dryRun: true });
                if (stale()) return;
                if (!res.ok) refused.push([q.n, res.message]);
            }
            if (refused.length) {
                refused.forEach(([n, message]) => patch(gen, n, { state: "failed", error: message }));
                setPhase("refused");
                return;
            }
            setPhase("running");
            for (const q of todo) {
                if (stale()) return;
                patch(gen, q.n, { state: "asking", error: null });
                let res = await correctQuestion(assessmentId, q.n, q.text);
                if (stale()) return;
                // Another correction still asking on this scan: wait for it, then send this one.
                for (let tries = 0; !res.ok && res.code === "ask_in_progress" && tries < 30; tries += 1) {
                    await sleep(POLL_MS);
                    if (stale()) return;
                    res = await correctQuestion(assessmentId, q.n, q.text);
                    if (stale()) return;
                }
                // The old answers could not be cleared: the same correction retries it, once.
                if (!res.ok && res.code === "clear_failed") {
                    res = await correctQuestion(assessmentId, q.n, q.text);
                    if (stale()) return;
                }
                if (!res.ok) {
                    patch(gen, q.n, { state: "failed", error: res.message });
                    continue;
                }
                let state = await waitFor(q.n, stale);
                if (state === "cancelled" || stale()) return;
                // Only a failed clear is sent again by itself: an engine failure is a paid retry, staff's call.
                if (state === "clear_failed") {
                    const again = await correctQuestion(assessmentId, q.n, q.text);
                    if (stale()) return;
                    state = again.ok ? await waitFor(q.n, stale) : "failed";
                    if (state === "cancelled" || stale()) return;
                }
                patch(gen, q.n, { state: state === "clear_failed" ? "failed" : state });
            }
            setPhase("done");
        } finally {
            if (!stale()) running.current = false;
        }
        // Rows and phase are read when the press is made.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assessmentId, questions, phase, rows]);

    return { phase, rows, confirming, setConfirming, run };
}

